import { useCreateInventoryItem } from "@/hooks/api-hooks";
import { Button, NumberInput, Textarea, TextInput } from "@mantine/core";
import { IconBuilding, IconFolderPlus, IconLink, IconUnlink } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { InventoryItem } from "@turizi/shared";
import BusinessSelector from "./business-selector";


type Props = {
    onCreate?: (data: InventoryItem) => void;
    parent?: InventoryItem;
}

export default function ModalNewInventoryItem({ onCreate, parent }: Props): JSX.Element {
    const { t } = useTranslation();
    const create = useCreateInventoryItem();

    const [draft, setDraft] = useState<Partial<InventoryItem>>({
        parent_id: parent?.id,
        business_id: parent?.business_id,
        quantity: 1,
        notes: null,
        capacity: null
    });

    const canSave = draft?.name?.trim().length || !!draft?.business_id;

    const createNewInventoryItem = async () => {
        if (!canSave) return;
        create.mutate(draft, {
            onSuccess: ({ data }) => {
                onCreate?.(data);
            }
        });
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && canSave) {
            createNewInventoryItem();
        }
    }

    useEffect(() => {
        setDraft({
            parent_id: parent?.id,
            business_id: parent?.business_id,
            quantity: 1,
            capacity: null
        });
    }, [parent]);

    return (
        <div className="space-y-2">
            {
                parent && (
                    <div className="solid-box-border-purple flex items-center gap-2 text-sm text-gray-600 font-bold">
                        <IconFolderPlus size={16} stroke={1} /> {parent?.name}
                    </div>
                )
            }
            {
                !draft?.business_id && (
                    <BusinessSelector
                        placeholder={(
                            <div className="text-sm text-gray-400 p-2 flex items-center gap-2">
                                <IconLink size={16} stroke={2} />
                                <div>{t('link_business')}</div>
                            </div>
                        )}
                        onSelect={(business) => {
                            setDraft({
                                ...draft,
                                business_id: business.id,
                                name: business.name,
                            });
                        }}
                    />
                )
            }
            <div className="flex items-center gap-2">
                <TextInput
                    className="w-full"
                    label={t('name')}
                    value={draft?.name || ''}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    rightSection={draft?.business_id ? <IconBuilding size={16} stroke={1} /> : null}
                />
                {draft?.business_id && (
                    <Button
                        className="mt-6"
                        color="black"
                        variant="subtle"
                        size="xs"
                        onClick={() => {
                            setDraft({ ...draft, business_id: null });
                        }}
                    >
                        <IconUnlink size={16} stroke={2} />
                    </Button>
                )}
            </div>
            <NumberInput
                className="flex-1"
                label={t('paxs_capacity')}
                description={t('paxs_capacity_description')}
                value={draft?.capacity}
                hideControls
                onChange={(value) => setDraft({ ...draft, capacity: value ? Number(value) : null })}
                onKeyDown={handleKeyDown}
            />
            <NumberInput
                className="flex-1"
                label={t('quantity')}
                description={t('quantity_description')}
                value={draft?.quantity}
                hideControls
                onChange={(value) => setDraft({ ...draft, quantity: value ? Number(value) : null })}
                onKeyDown={handleKeyDown}
            />
            <Textarea
                label={t('notes')}
                value={draft?.notes || ''}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                minRows={2}
                autosize
                resize='vertical'
            />
            <div className="flex justify-end mt-8">
                <Button
                    tabIndex={0}
                    onClick={createNewInventoryItem}
                    loading={create.isPending}
                    disabled={!canSave}
                    size="xs"
                    color="black"
                    radius="lg"
                >
                    {t('save')}
                </Button>
            </div>
        </div>
    );
}